import React, { useState } from 'react';
import moment from 'moment';

import { Box, Button, CircularProgress, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CancelOutlined, CheckCircleOutlineOutlined, CloudUpload, ErrorOutlineOutlined, QueryBuilderOutlined, Refresh } from '@mui/icons-material';
import { ReactSVG } from 'react-svg';
import { useIntl } from 'react-intl';
import messages from '../messages';

import { getStyles } from './styles';
import { IconSvg } from '../../../../../utils/globalIcons';
import { tostAlert } from '../../../../../utils/AlertToast';
import GeneralTable from '../../../../../components/GeneralTable';
import { apiErrorHandler } from '../../../../../utils/ApiErrorHandler';

import AnalyticsFilesApi from '../../../../../Api/Services/AppflyteBackend/AnalyticsFiles';
import { useAppContext } from '../../../../../context/AppContext';
import { getUserName } from '../../../../../utils/GetAccountDetails';
import AnalyticsApi from '../../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import handleFileUpload from '../../../../../utils/fileUpload';

let analyitics_file_path = `ameya_analytics/analytics_files`

function Files({ fileData, setFileData, exceAnalytics, onReload }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const intl = useIntl();

    const { selectedProject, handleAnalyticsSteps } = useAppContext();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false)

    let uploadProgress = []
    const [uploadProgressState, setUploadProgress] = useState([])

    const getUniqueFilename = (filename, existingFilenames) => {
        if (!existingFilenames?.length) return filename;

        const lastDotIndex = filename.lastIndexOf(".");
        const hasExtension = lastDotIndex > 0;

        const baseName = hasExtension ? filename.substring(0, lastDotIndex) : filename;
        const extension = hasExtension ? filename.substring(lastDotIndex) : "";

        let counter = 1;
        let newName = filename;

        while (existingFilenames.includes(newName)) {
            newName = `${baseName} (${counter})${extension}`;
            counter++;
        }

        return newName;
    };

    const handleFileChange = async (event) => {
        try {
            event.preventDefault();
            event.stopPropagation();

            const files = Array.from(event.target.files);

            const maxFileSize = 40 * 1024 * 1024;
            const validFiles = files.filter(file => file.size <= maxFileSize) ?? [];

            if (validFiles.length < files.length) {
                tostAlert(intl.formatMessage(messages.file_size_error), 'warning');
                return;
            }

            const existingFileNames = (fileData?.fileList || [])?.map(f => f?.payload?.file_name)?.filter(Boolean) ?? [];
            const alreadySelected = selectedFiles.map(f => f.name);
            const allExisting = [...existingFileNames, ...alreadySelected];

            const renamedFiles = validFiles.map(file => {
                const newName = getUniqueFilename(file.name, allExisting);
                allExisting.push(newName);

                return new File([file], newName, { type: file.type });
            });

            event.target.value = null;
            setSelectedFiles(renamedFiles);

        } catch (error) {
            console.log(error);
        }
    };

    const handleFileDrop = async (event) => {
        try {
            event.preventDefault();
            event.stopPropagation();

            const files = Array.from(event.dataTransfer.files);

            const maxFileSize = 40 * 1024 * 1024;
            const validFiles = files.filter(file => file.size <= maxFileSize) ?? [];

            if (validFiles.length < files.length) {
                tostAlert(intl.formatMessage(messages.file_size_error), 'warning');
                return;
            }

            const existingFileNames = (fileData?.fileList || [])?.map(f => f?.payload?.file_name)?.filter(Boolean) ?? [];
            const alreadySelected = selectedFiles.map(f => f.name);

            const allExisting = [...existingFileNames, ...alreadySelected];

            const renamedFiles = validFiles.map(file => {
                const newName = getUniqueFilename(file.name, allExisting);
                allExisting.push(newName);
                return new File([file], newName, { type: file.type });
            });

            event.target.value = null;
            setSelectedFiles(renamedFiles);

        } catch (error) {
            console.log(error);
        }
    };

    const handleFileRemove = (index) => {
        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(index, 1);
        setSelectedFiles(updatedFiles);
    }

    const saveInAmeyaBackend = async (item) => {
        try {
            const project_id = selectedProject?.payload?.__auto_id__ ?? null;
            const userName = getUserName();
            const dataset_id = JSON.parse(localStorage.getItem('dataset_id')) ?? null;

            const reqObj = {
                "collection_item": {
                    "file_name": item?.fileName,
                    "file_id": item?.fileId,
                    "file_url": item?.fileUrl,
                    "dataset_id": dataset_id,
                    "project_id": project_id,
                    "uploaded_by": userName,
                    "uploaded_via": 'api',
                    "status": 'Processing',
                    "new_file": true,
                    "checksum": null,
                    "uploaded_on": moment().format("DD-MM-YYYY HH:mm:ss"),
                    "__auto_id__": item?.fileId
                }
            }
            const upload_response = await AnalyticsFilesApi.addFile(JSON.stringify(reqObj));
            return upload_response
        } catch (error) {
            return error
        }
    }

    const markIndexFailed = (index, errorMessage) => {
        const tempLoadArr = [...uploadProgress]
        tempLoadArr[index] = { status: 'failed', message: errorMessage };
        uploadProgress = tempLoadArr
        setUploadProgress([...tempLoadArr])
    }

    const markIndexSuccess = (index) => {
        const tempLoadArr = [...uploadProgress]
        tempLoadArr[index] = { status: 'success', message: "Upload and save successful" };
        uploadProgress = tempLoadArr
        setUploadProgress([...tempLoadArr])
    }

    const markIndexSaving = (index) => {
        const tempLoadArr = [...uploadProgress]
        tempLoadArr[index] = { status: 'saving', message: "Saving to backend..." };
        uploadProgress = tempLoadArr
        setUploadProgress([...tempLoadArr])
    }

    const startUploading = async () => {
        if ((selectedFiles || [])?.length === 0) {
            tostAlert(intl.formatMessage(messages.file_selection_warning), "warning")
            return
        }

        setLoading(true)

        try {
            const tempArr = selectedFiles.map(file => ({ status: 'pending', message: "Waiting to upload" }));
            setUploadProgress([...tempArr]);
            uploadProgress = tempArr;

            let successfulUploads = 0;
            let failedUploads = 0;

            for (const [index, file] of selectedFiles.entries()) {
                try {
                    const tempLoadArr = [...uploadProgress];
                    tempLoadArr[index] = { status: 'uploading', message: "Uploading the file" };
                    setUploadProgress(tempLoadArr);
                    uploadProgress = tempLoadArr;

                    const file_name = file?.name ?? `file_${index}`

                    const file_path = `${analyitics_file_path}/${file_name}`
                    const file_upload_response = await handleFileUpload(file_path, file);

                    if (file_upload_response.fileUrl && file_upload_response.fileId && file_upload_response.fileName) {
                        markIndexSaving(index);

                        try {
                            const backendResponse = await saveInAmeyaBackend(file_upload_response);

                            if (backendResponse && backendResponse.status === 200) {
                                markIndexSuccess(index);
                                successfulUploads++;
                            } else {
                                markIndexFailed(index, 'Failed to save to backend');
                                failedUploads++;
                            }
                        } catch (backendError) {
                            console.error(`Failed to save file ${file.name} to backend:`, backendError);
                            markIndexFailed(index, 'Backend save failed');
                            failedUploads++;
                        }
                    } else {
                        markIndexFailed(index, 'Upload failed');
                        failedUploads++;
                    }
                } catch (err) {
                    markIndexFailed(index, 'Upload failed');
                    failedUploads++;
                    console.error(`Failed to upload file at index ${index}`, err);
                }
            }

            if (successfulUploads > 0) {
                handleAnalyticsSteps('analytics_file_dataset_and_upload_status')
                setFileData(prev => ({ ...prev, fileAdded: true }));
                if (failedUploads === 0) {
                    tostAlert(`All ${successfulUploads} files uploaded successfully!`, 'success');
                } else {
                    tostAlert(`${successfulUploads} files uploaded successfully, ${failedUploads} failed`, 'warning');
                }
            } else if (failedUploads > 0) {
                tostAlert(intl.formatMessage(messages.file_upload_error), 'error');
            }

        } catch (error) {
            console.log(error)
            apiErrorHandler(error)
        } finally {
            setLoading(false)
            setTimeout(() => {
                uploadProgress = []
                setUploadProgress([])
                setSelectedFiles([])
            }, 3000);
        }
    }

    const closeUploadIndicator = () => {
        setLoading(false)
        setUploadProgress([])
        uploadProgress = []
        setSelectedFiles([])
    }

    const handleSourceFileDeletion = async (item) => {
        const confirmDelete = window.confirm(intl.formatMessage(messages.file_delete_warning));
        if (!confirmDelete) return;

        setFileData(prev => ({ ...prev, loading: true }));
        try {

            const dataset_id = JSON.parse(localStorage.getItem('dataset_id')) ?? null;
            const new_file_status = item?.payload?.new_file ?? true;
            const checksum = item?.payload?.checksum ?? null;

            if (new_file_status === false && checksum) {
                try {
                    await AnalyticsApi.deleteFileDatasource(dataset_id, checksum)
                } catch (error) {
                    console.log(error)
                }
            }

            const item_id = item?.payload?.__auto_id__ ?? null
            const response = await AnalyticsFilesApi.delete(item_id);
            if (response.status === 200) {
                onReload(fileData?.fileConnectorData);
                tostAlert(intl.formatMessage(messages.file_delete_success), 'success');
            } else {
                tostAlert(intl.formatMessage(messages.file_delete_error), 'error');
            }

        } catch (error) {
            console.error(error);
            apiErrorHandler(error);
        } finally {
            setFileData(prev => ({ ...prev, loading: false }));
        }
    };


    const columns = [
        {
            id: 'payload.file_name',
            label: 'Document',
            sortable: true,
            renderCell: (row) => {
                const fileName = row?.payload?.file_name || '';
                const displayName =
                    fileName.length > 40 ? `${fileName.slice(0, 40)}...` : fileName;

                return (
                    <Box display="flex" alignItems="center">
                        <Tooltip
                            title={
                                fileName.length > 40 ? (
                                    <Box p={1}>
                                        <Typography sx={{ ...styles.paraText }}>
                                            {fileName}
                                        </Typography>
                                    </Box>
                                ) : ''
                            }
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    width: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginRight: '8px',
                                    }}
                                >
                                    <ReactSVG
                                        src={IconSvg.filesIcon}
                                        beforeInjection={(svg) => {
                                            svg.setAttribute(
                                                'style',
                                                'width:24px; height:24px; display:block;'
                                            );
                                            svg.setAttribute('stroke', '#000000');
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        ...styles.paraText,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                    }}
                                >
                                    {displayName}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            id: 'payload.uploaded_on',
            label: 'Created On',
            sortable: true,
            renderCell: (row) => row?.payload.uploaded_on?.substring(0, 10)
        },
        {
            id: 'payload.status',
            label: 'Status',
            sortable: true,
            renderCell: (row) => {
                return (<Box>
                    <ReactSVG
                        src={{
                            Processing: IconSvg.trainingIcon,
                            Failed: IconSvg.errorIcon,
                            Success: IconSvg.trainedIcon,
                        }[row?.payload.status || 'Processing']}
                        beforeInjection={(svg) => {
                            svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                            svg.setAttribute('stroke', '#000000');
                        }}
                    />
                    <Typography sx={styles.paraText} marginLeft={'25px'} marginTop={'-22px'}>
                        {row?.payload.status ?? 'Processing'}
                    </Typography>
                </Box>)
            }
        },
        {
            id: 'payload.uploaded_by',
            label: 'Uploaded By',
            sortable: true,
            renderCell: (row) => row?.payload.uploaded_by
        },
        {
            id: '',
            label: '',
            sortable: false,
            renderCell: (row) => (
                <ReactSVG
                    src={IconSvg.deleteIcon}
                    className="delete_icon"
                    onClick={() => handleSourceFileDeletion(row)}
                />
            ),
        },
    ];

    return (
        <Box>

            {fileData.errorStatus &&
                (<Typography sx={{ fontSize: '14px', fontWeight: '400', color: "#C30E2E" }}>
                    {fileData.errorMessage}
                </Typography>)
            }

            <Box marginTop={'20px'} sx={styles.fileUploadContainer}>

                {uploadProgressState && uploadProgressState.length > 0
                    ?
                    <Box sx={styles.uploadLoadingBox}>
                        <Box sx={styles.uploadingHeaderBox}>
                            <Typography sx={styles.uploadingText}>Processing files …</Typography>
                            {!loading ? <CancelOutlined sx={styles.closeIcon} onClick={closeUploadIndicator} /> : ''}
                        </Box>
                        <Box sx={styles.fileListContainer}>
                            {
                                selectedFiles.map((file, index) =>
                                    <Box key={"upload_progress_ind_key_" + index}>
                                        {
                                            index > 0 ? <Divider width="100%" /> : ''
                                        }
                                        <Box display="flex" height="40px" width="100%" alignItems="center" paddingX="10px">
                                            {
                                                ({
                                                    'uploading': <CircularProgress size="18px" sx={{ ...styles.tickIcon, color: '#0B51C5' }} />,
                                                    'saving': <CircularProgress size="18px" sx={{ ...styles.tickIcon, color: '#FF9800' }} />,
                                                    'pending': <QueryBuilderOutlined sx={{ ...styles.tickIcon, color: '#1976d2' }} />,
                                                    'success': <CheckCircleOutlineOutlined sx={{ ...styles.tickIcon, color: '#4CAF50' }} />,
                                                    'failed': <ErrorOutlineOutlined sx={styles.failedIcon} />
                                                }[uploadProgressState[index]?.status])
                                            }
                                            <Box display="flex" flexDirection="column" width="100%">
                                                <Typography noWrap sx={styles.dropZoneFont}>{file?.name}</Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    {uploadProgressState[index]?.message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )
                            }
                        </Box>
                    </Box>
                    :
                    ''
                }

                <Box
                    onDrop={handleFileDrop}
                    onDragOver={(e) => e.preventDefault()}
                >
                    <input
                        type="file"
                        accept={exceAnalytics ? ".xlsx" : ".pdf, .doc, .docx, .md, .mdx"}
                        onChange={handleFileChange}
                        style={{ display: "none", cursor: "pointer" }}
                        id="fileInput"
                        multiple
                    />
                    <label
                        id='fileInput'
                        htmlFor="fileInput">
                        {selectedFiles.length === 0 &&
                            <Box>
                                <Typography sx={styles.fileDropLabelText}>Drag and drop files or&nbsp;<span style={{ color: '#0B51C5', cursor: 'pointer' }}>click to upload</span> </Typography>
                                <Typography sx={styles.paraText}>
                                    Up to 40 MB in size ({exceAnalytics ? "xlsx" : "pdf, doc, docx, md, mdx"})
                                </Typography>
                            </Box>
                        }
                        <IconButton component="span">
                            <CloudUpload style={{ color: "black", width: "32px", height: "32px" }} />
                        </IconButton>
                    </label>
                </Box>

                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} flexWrap="wrap" >
                    {(selectedFiles || [])?.length > 0 && selectedFiles.map((file, index) => (
                        < Box key={index} display={'flex'} justifyContent={'center'} alignItems={'center'} alignContent={'center'} textAlign={'center'} padding={'3px 15px 3px 15px'} margin={'5px'} backgroundColor={'#FFFFFF'} borderRadius={'20px'}>
                            <Typography sx={styles.paraText}>{file.name}</Typography>
                            <Box marginLeft={'10px'} onClick={() => handleFileRemove(index)}>
                                <ReactSVG src={IconSvg.deleteIcon} className='delete_icon' />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box >

            <Box marginTop={'20px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <Button
                    sx={styles.uploadBtn}
                    disabled={loading}
                    onClick={startUploading}
                >
                    <Typography style={styles.btnText}>Upload</Typography>
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                {!fileData.loading ?
                    (<IconButton onClick={() => onReload(fileData?.fileConnectorData)}>
                        <Refresh />
                    </IconButton>)
                    :
                    (<CircularProgress size={'20px'} />)
                }
            </Box>

            <Box marginTop={'5px'}>
                <GeneralTable
                    data={fileData?.fileList || []}
                    columns={columns}
                    loading={fileData?.loading}
                />
            </Box>

        </Box >
    )
}

export default Files